import { isEmpty, isPresent } from '@ember/utils';
import type { SelectOption } from './form-component-utils';

export type PropertyType =
  | 'string'
  | 'number'
  | 'amount'
  | 'boolean'
  | 'list'
  | 'date'
  | 'contactIds'
  | 'giftHistory'
  | 'distanceFrom'
  | 'no-operation';

// example of no operation is "Show Hidden Contacts"

export type Operation =
  | '='
  | '!='
  | 'stringContains'
  | 'absent'
  | 'present'
  | '>'
  | '>='
  | '<'
  | '<='
  | '><' // date between
  | 'any' // list has any
  | 'all' // list has all
  | 'none'; // list has none

export const OperationLabels: Record<Operation, string> = {
  '=': 'is',
  '!=': 'is not',
  stringContains: 'contains',
  absent: 'is absent',
  present: 'is present',
  '>': '>', // TODO: Make operation labels specific to date propertyTypes
  '>=': '>=',
  '<': '<',
  '<=': '<=',
  '><': 'between',
  any: 'in any',
  all: 'in all',
  none: 'not in',
};

export const OperationLabelOverridesForDate: {
  [operation in Operation]+?: string;
} = {
  '>=': 'after',
  '<=': 'before',
};

export const OperationLabelOverridesForGiftHistory: {
  [operation in Operation]+?: string;
} = {
  '>=': 'at least',
  '<=': 'at most',
  '=': 'exactly',
  absent: 'nothing',
};

export const OperationLabelOverridesForDistanceFrom: {
  [operation in Operation]?: string;
} = {
  '<=': 'within',
};

export function GetOperationLabel(
  operation: Operation,
  options: { propertyType?: PropertyType } = {},
): string {
  if (options.propertyType === 'date') {
    return (
      OperationLabelOverridesForDate[operation] || OperationLabels[operation]
    );
  } else if (options.propertyType === 'giftHistory') {
    return (
      OperationLabelOverridesForGiftHistory[operation] ||
      OperationLabels[operation]
    );
  } else if (options.propertyType === 'distanceFrom') {
    return (
      OperationLabelOverridesForDistanceFrom[operation] ||
      OperationLabels[operation]
    );
  }

  return OperationLabels[operation];
}

export const AvailableOperationsByPropertyType: Record<
  PropertyType,
  Operation[]
> = {
  string: ['stringContains', '=', '!=', 'present', 'absent'],
  number: ['=', '!=', '>', '>=', '<', '<=', 'present', 'absent'],
  amount: ['=', '!=', '>', '>=', '<', '<=', 'present', 'absent'],
  boolean: ['=', 'present', 'absent'],
  // if listIsSingleSelection is true then 'all' is removed
  list: ['any', 'all', 'none', 'present', 'absent'],
  date: ['=', '>=', '<=', '><', 'present', 'absent'],
  giftHistory: ['>=', '<=', '=', 'absent'],
  distanceFrom: ['<='],
  contactIds: ['any'],
  'no-operation': [],
};

export const AvailableOperationsForSingleSelectionList: Operation[] =
  AvailableOperationsByPropertyType.list.filter((x) => x !== 'all');

export const OperationsWithoutVal: { [operation in Operation]+?: boolean } = {
  absent: true,
  present: true,
};

// This is for the available filters and their options
export interface PropertyFilterInfo {
  propertyName: string;
  label: string;
  propertyType: PropertyType;
  availableOperations?: Operation[]; // lists the available operations. If not present, it uses AvailableOperationsByPropertyType above
  listOptions?: string[] | SelectOption[]; // populated with available options when the propertyType is 'list'
  listIsSingleSelection?: boolean; // true if the user can only select one value
  custom?: any; // allows for any custom information to be included with the filter. The ContactsCache uses it for attaching the filtering function to it
  systemOnly?: boolean; // true if the filter can only be added by the system and is not shown to the user
}

// `applyToQueryFilter` is used to build up the query object that will be sent to the endpoint
export interface PropertyFilterInfoForQueryFilter extends PropertyFilterInfo {
  applyToQueryFilter: (
    appliedFilter: AppliedPropertyFilter,
    queryFilter: any,
  ) => Promise<void>;
}

// returns the common contact ids in 2 arrays
// if the first array is undefined then it returns the second array
// typically used in `applyToQueryFilter` to handle the scenario where multiple applied filters
// use contactsCache.filter()
// example:
//   applyToQueryFilter: async (appliedFilter: AppliedPropertyFilter, queryFilter: any) => {
//     let contactIds = (await this.contactsCache.filter([appliedFilter])).map(x => x.id);
//     queryFilter.contactIds = ContactIdsIntersection(queryFilter.contactIds, contactIds);
//   }
export function ContactIdsIntersection(
  contactIdsOne: string[] | undefined,
  contactIdsTwo: string[],
) {
  if (contactIdsOne == null) {
    return contactIdsTwo;
  }
  return (contactIdsOne || []).filter((x) => contactIdsTwo.includes(x));
}

export interface GiftHistoryVal {
  val: number;
  valType: 'amount' | 'count';
  from: string; // 'YYYY-MM-DD' format
  to: string; // 'YYYY-MM-DD'
}

export interface DistanceFromVal {
  distance: number;
  units: string; // miles
  location: string; // address or city/state
}

export interface AppliedPropertyFilter {
  propertyName: string;
  operation?: Operation;
  val?: string | string[] | boolean | GiftHistoryVal | DistanceFromVal; // when propertyType is list then val is string[]
}

export const OperationFilters = {
  absent: (val: any) => isEmpty(val),
  present: (val: any) => isPresent(val),
  stringContains: (filterValLowerCase: string, val: string | undefined) => {
    return val && val.indexOf(filterValLowerCase) >= 0;
  },
  stringEquals: (filterValLowerCase: string, val: string | undefined) => {
    return val?.toLowerCase() === filterValLowerCase;
  },
};

// TODO: think through ways to shorten the serialized string
export function serializeAppliedFilters(
  filters: AppliedPropertyFilter[] | undefined,
): string | undefined {
  if (filters == null || filters.length === 0) {
    return undefined;
  }
  return JSON.stringify(filters);
}

export function deserializeAppliedFilters(
  serializedFilters: string | undefined,
): AppliedPropertyFilter[] | undefined {
  if (serializedFilters == null || serializedFilters == '') {
    return undefined;
  }
  return JSON.parse(serializedFilters!) as AppliedPropertyFilter[];
}
